import { projectId, dataset } from './api';

/**
 * Get the proper download URL for a Sanity file asset
 * Sanity files use a specific URL format that we need to construct correctly
 */
export const getFileUrl = (fileRef: string): string => {
  if (!fileRef) return '#';

  // Debug: Check if environment variables are loaded

  if (!projectId || !dataset) {
    console.error('Missing environment variables for Sanity configuration');
    return '#';
  }

  try {
    // Sanity file references are in the format: "file-{fileId}-{extension}"

    if (!fileRef.startsWith('file-')) {
      return '#';
    }

    // Extract the file ID and extension
    const parts = fileRef.split('-');

    if (parts.length < 3) {
      return '#';
    }

    // The file ID is everything between "file-" and the last part (extension)
    const fileId = parts.slice(1, -1).join('-');
    const extension = parts[parts.length - 1];

    if (!fileId || !extension) {
      return '#';
    }

    // Construct the proper Sanity file URL
    // Format: https://cdn.sanity.io/files/{projectId}/{dataset}/{fileId}.{extension}
    const fileUrl = `https://cdn.sanity.io/files/${projectId}/${dataset}/${fileId}.${extension}`;
    return fileUrl;
  } catch (error) {
    console.error('Error constructing file URL:', error);
    return '#';
  }
};

/**
 * Alternative method to get file URL using Sanity's asset URL format
 * This might work better for some file types
 */
export const getAlternativeFileUrl = (fileRef: string): string => {
  if (!fileRef) return '#';

  try {
    // Try using the asset URL format: https://cdn.sanity.io/files/{projectId}/{dataset}/{fileRef}
    const fileUrl = `https://cdn.sanity.io/files/${projectId}/${dataset}/${fileRef}`;
    return fileUrl;
  } catch (error) {
    return error as string;
  }
};

/**
 * More flexible file URL generation that tries multiple approaches
 */
export const getFlexibleFileUrl = (fileRef: string): string => {
  if (!fileRef) {
    return '#';
  }

  if (!projectId || !dataset) {
    return '#';
  }

  try {
    // Try different approaches based on the file reference format

    // Approach 1: Standard file-{id}-{ext} format
    if (fileRef.startsWith('file-')) {
      const parts = fileRef.split('-');

      if (parts.length >= 3) {
        const fileId = parts.slice(1, -1).join('-');
        const extension = parts[parts.length - 1];
        const url = `https://cdn.sanity.io/files/${projectId}/${dataset}/${fileId}.${extension}`;
        return url;
      } else {
        console.warn(`File reference has ${parts.length} parts, expected at least 3`);
      }
    }

    // Approach 2: Direct asset reference (some projects use this)
    const directUrl = `https://cdn.sanity.io/files/${projectId}/${dataset}/${fileRef}`;

    // For now, return the direct URL and let the download logic handle it
    return directUrl;
  } catch (error) {
    console.error('Error in flexible file URL generation:', error);
    return '#';
  }
};

/**
 * Get the file extension from a file reference
 */
export const getFileExtension = (fileRef: string): string => {
  if (!fileRef) return '';

  const parts = fileRef.split('-');
  return parts[parts.length - 1] || '';
};

/**
 * Get the original filename from a file asset
 */
export const getOriginalFilename = (fileAsset: any): string => {
  return fileAsset?.originalFilename || fileAsset?.label || 'download';
};

/**
 * Enhanced file download function that tries multiple URL formats
 */
export const getBestFileUrl = (fileRef: string): string => {
  const primaryUrl = getFileUrl(fileRef);

  return primaryUrl;
};

/**
 * Validate if a file URL is accessible and returns the correct content type
 * This helps identify if Sanity is serving files correctly
 */
export const validateFileUrl = async (
  url: string,
): Promise<{ isValid: boolean; contentType?: string; error?: string }> => {
  if (!url || url === '#') {
    return { isValid: false, error: 'Invalid URL' };
  }

  try {
    const response = await fetch(url, { method: 'HEAD' });

    if (!response.ok) {
      return {
        isValid: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const contentType = response.headers.get('content-type') || undefined;

    // Check if content type suggests it's an HTML file (which would be wrong for downloads)
    if (contentType && contentType.includes('text/html')) {
      console.warn(`Warning: File URL ${url} is serving HTML content instead of file content`);
      return {
        isValid: false,
        contentType,
        error: 'File is serving HTML content instead of file content',
      };
    }

    return { isValid: true, contentType };
  } catch (error) {
    console.error(`Error validating file URL ${url}:`, error);
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Test different Sanity file URL formats to find the correct one
 * This helps debug file download issues
 */
export const testFileUrlFormats = (fileRef: string): string[] => {
  if (!fileRef || !projectId || !dataset) {
    return [];
  }

  const urls: string[] = [];

  try {
    // Format 1: Standard Sanity CDN format
    if (fileRef.startsWith('file-')) {
      const parts = fileRef.split('-');
      if (parts.length >= 3) {
        const fileId = parts.slice(1, -1).join('-');
        const extension = parts[parts.length - 1];
        urls.push(`https://cdn.sanity.io/files/${projectId}/${dataset}/${fileId}.${extension}`);
      }
    }

    // Format 2: Direct asset reference format
    urls.push(`https://cdn.sanity.io/files/${projectId}/${dataset}/${fileRef}`);

    // Format 3: Alternative CDN format (some projects use different domains)
    urls.push(`https://cdn.sanity.io/files/${projectId}/${dataset}/${fileRef}`);
  } catch (error) {
    console.error('Error in testFileUrlFormats:', error);
  }

  return urls;
};

/**
 * Handle different types of Sanity file assets
 */
export const resolveFileAsset = (fileAsset: any): string => {
  if (!fileAsset) {
    return '#';
  }

  // Check if it's a resolved asset (has url property)
  if (fileAsset.url) {
    return fileAsset.url;
  }

  // Check if it's a reference (has _ref property)
  if (fileAsset._ref) {
    return getFlexibleFileUrl(fileAsset._ref);
  }

  // Check if it's a resolved asset with _id
  if (fileAsset._id) {
    // Try to construct URL from ID
    return `https://cdn.sanity.io/files/${projectId}/${dataset}/${fileAsset._id}`;
  }

  return '#';
};
